import { keys } from "@/lib/i18n/portal";
import type { useTranslations } from "@/lib/i18n/intl";
import type {
  CouponDiscountUnit,
  CouponKind,
  CouponView,
  PromotionScope,
} from "@/lib/api/types";

type Translate = ReturnType<typeof useTranslations>;

/** Percent discounts (cart / product) may not exceed this. */
const PERCENT_MAX = 100;

/**
 * Editable state for the shared promotion fields (kind, value, BOGO, scope).
 * Numeric inputs are kept as strings while typing and parsed on submit.
 */
export type PromotionState = {
  kind: CouponKind;
  discountUnit: CouponDiscountUnit;
  discountValue: string;
  buyQuantity: string;
  getQuantity: string;
  getDiscountPercent: string;
  appliesTo: PromotionScope;
  productIds: string[];
  categoryIds: string[];
};

export type PromotionFieldErrors = {
  discountValue?: string;
  buyQuantity?: string;
  getQuantity?: string;
  getDiscountPercent?: string;
  scope?: string;
};

/**
 * Resolved fields for the create/update payload. Every field is always present
 * (irrelevant ones nulled / emptied) so the same object works for POST + PATCH.
 */
export type PromotionPayloadFields = {
  kind: CouponKind;
  discountUnit: CouponDiscountUnit | null;
  discountValue: number | null;
  buyQuantity: number | null;
  getQuantity: number | null;
  getDiscountPercent: number | null;
  appliesTo: PromotionScope;
  productIds: string[];
  categoryIds: string[];
};

/**
 * The subset of i18n keys the shared promotion UI + validation need. Both the
 * `keys.coupons` and `keys.discounts` namespaces structurally satisfy this, so
 * either can be passed in.
 */
export type PromotionFieldKeys = {
  kindLabel: string;
  kindCartDiscount: string;
  kindProductDiscount: string;
  kindFreeDelivery: string;
  kindBogo: string;
  discountUnitLabel: string;
  discountUnitPercent: string;
  discountUnitFixed: string;
  discountValueLabel: string;
  discountValuePercentPlaceholder: string;
  discountValueFixedPlaceholder: string;
  bogoBuyQtyLabel: string;
  bogoBuyQtyPlaceholder: string;
  bogoGetQtyLabel: string;
  bogoGetQtyPlaceholder: string;
  bogoGetPercentLabel: string;
  bogoGetPercentHint: string;
  bogoGetPercentPlaceholder: string;
  scopeLabel: string;
  scopeAll: string;
  scopeProducts: string;
  scopeCategories: string;
  scopeProductsLabel: string;
  scopeCategoriesLabel: string;
  scopePickerPlaceholder: string;
  scopeSearchPlaceholder: string;
  scopeNoResults: string;
  scopeProductsEmpty: string;
  scopeCategoriesEmpty: string;
  scopeLoading: string;
  errorDiscountValueRequired: string;
  errorDiscountPercentMax: string;
  errorBogoBuyQtyInvalid: string;
  errorBogoGetQtyInvalid: string;
  errorBogoPercentInvalid: string;
  errorScopeRequired: string;
};

/** Kinds that carry a discountUnit + discountValue. */
export function kindUsesValue(kind: CouponKind): boolean {
  return kind === "CART_DISCOUNT" || kind === "PRODUCT_DISCOUNT";
}

/** Kinds that can be scoped to specific products / categories. */
export function kindUsesScope(kind: CouponKind): boolean {
  return kind === "PRODUCT_DISCOUNT" || kind === "BUY_X_GET_Y";
}

/** Derive the editable promotion state from an existing coupon (or defaults). */
export function initialPromotionState(coupon?: CouponView): PromotionState {
  const numToStr = (value: number | null | undefined): string =>
    value !== null && value !== undefined ? String(value) : "";
  return {
    kind: coupon?.kind ?? "CART_DISCOUNT",
    discountUnit: coupon?.discountUnit ?? "PERCENT",
    discountValue: numToStr(coupon?.discountValue),
    buyQuantity: numToStr(coupon?.buyQuantity),
    getQuantity: numToStr(coupon?.getQuantity),
    getDiscountPercent: numToStr(coupon?.getDiscountPercent),
    appliesTo: coupon?.appliesTo ?? "ALL",
    productIds: coupon?.productIds ?? [],
    categoryIds: coupon?.categoryIds ?? [],
  };
}

function isPositiveInt(value: string): boolean {
  const n = Number(value);
  return value.trim().length > 0 && Number.isInteger(n) && n >= 1;
}

/**
 * Validate the shared promotion fields for the active kind. Returns `null` when
 * everything is valid, otherwise a map of per-field messages.
 */
export function validatePromotionState(
  state: PromotionState,
  t: Translate,
  k: PromotionFieldKeys,
): PromotionFieldErrors | null {
  const errors: PromotionFieldErrors = {};

  if (kindUsesValue(state.kind)) {
    const numericValue = Number(state.discountValue);
    if (
      state.discountValue.trim().length === 0 ||
      Number.isNaN(numericValue) ||
      numericValue <= 0
    ) {
      errors.discountValue = t(k.errorDiscountValueRequired);
    } else if (state.discountUnit === "PERCENT" && numericValue > PERCENT_MAX) {
      errors.discountValue = t(k.errorDiscountPercentMax);
    }
  }

  if (state.kind === "BUY_X_GET_Y") {
    if (!isPositiveInt(state.buyQuantity)) {
      errors.buyQuantity = t(k.errorBogoBuyQtyInvalid);
    }
    if (!isPositiveInt(state.getQuantity)) {
      errors.getQuantity = t(k.errorBogoGetQtyInvalid);
    }
    const percent = Number(state.getDiscountPercent);
    if (
      state.getDiscountPercent.trim().length === 0 ||
      Number.isNaN(percent) ||
      percent < 0 ||
      percent > PERCENT_MAX
    ) {
      errors.getDiscountPercent = t(k.errorBogoPercentInvalid);
    }
  }

  if (kindUsesScope(state.kind)) {
    if (state.appliesTo === "PRODUCTS" && state.productIds.length === 0) {
      errors.scope = t(k.errorScopeRequired);
    } else if (
      state.appliesTo === "CATEGORIES" &&
      state.categoryIds.length === 0
    ) {
      errors.scope = t(k.errorScopeRequired);
    }
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

/**
 * Resolve the promotion state into the payload fields, nulling / emptying any
 * fields that don't apply to the active kind.
 */
export function buildPromotionPayloadFields(
  state: PromotionState,
): PromotionPayloadFields {
  const usesValue = kindUsesValue(state.kind);
  const isBogo = state.kind === "BUY_X_GET_Y";
  const usesScope = kindUsesScope(state.kind);
  const appliesTo: PromotionScope = usesScope ? state.appliesTo : "ALL";

  return {
    kind: state.kind,
    discountUnit: usesValue ? state.discountUnit : null,
    discountValue: usesValue ? Number(state.discountValue) : null,
    buyQuantity: isBogo ? Number(state.buyQuantity) : null,
    getQuantity: isBogo ? Number(state.getQuantity) : null,
    getDiscountPercent: isBogo ? Number(state.getDiscountPercent) : null,
    appliesTo,
    productIds: usesScope && appliesTo === "PRODUCTS" ? state.productIds : [],
    categoryIds:
      usesScope && appliesTo === "CATEGORIES" ? state.categoryIds : [],
  };
}

/** Convenience accessor for the coupon-namespace promotion keys. */
export const couponPromotionKeys: PromotionFieldKeys = {
  kindLabel: keys.coupons.kindLabel,
  kindCartDiscount: keys.coupons.kindCartDiscount,
  kindProductDiscount: keys.coupons.kindProductDiscount,
  kindFreeDelivery: keys.coupons.kindFreeDelivery,
  kindBogo: keys.coupons.kindBogo,
  discountUnitLabel: keys.coupons.discountUnitLabel,
  discountUnitPercent: keys.coupons.discountUnitPercent,
  discountUnitFixed: keys.coupons.discountUnitFixed,
  discountValueLabel: keys.coupons.discountValueLabel,
  discountValuePercentPlaceholder: keys.coupons.discountValuePercentPlaceholder,
  discountValueFixedPlaceholder: keys.coupons.discountValueFixedPlaceholder,
  bogoBuyQtyLabel: keys.coupons.bogoBuyQtyLabel,
  bogoBuyQtyPlaceholder: keys.coupons.bogoBuyQtyPlaceholder,
  bogoGetQtyLabel: keys.coupons.bogoGetQtyLabel,
  bogoGetQtyPlaceholder: keys.coupons.bogoGetQtyPlaceholder,
  bogoGetPercentLabel: keys.coupons.bogoGetPercentLabel,
  bogoGetPercentHint: keys.coupons.bogoGetPercentHint,
  bogoGetPercentPlaceholder: keys.coupons.bogoGetPercentPlaceholder,
  scopeLabel: keys.coupons.scopeLabel,
  scopeAll: keys.coupons.scopeAll,
  scopeProducts: keys.coupons.scopeProducts,
  scopeCategories: keys.coupons.scopeCategories,
  scopeProductsLabel: keys.coupons.scopeProductsLabel,
  scopeCategoriesLabel: keys.coupons.scopeCategoriesLabel,
  scopePickerPlaceholder: keys.coupons.scopePickerPlaceholder,
  scopeSearchPlaceholder: keys.coupons.scopeSearchPlaceholder,
  scopeNoResults: keys.coupons.scopeNoResults,
  scopeProductsEmpty: keys.coupons.scopeProductsEmpty,
  scopeCategoriesEmpty: keys.coupons.scopeCategoriesEmpty,
  scopeLoading: keys.coupons.scopeLoading,
  errorDiscountValueRequired: keys.coupons.errorDiscountValueRequired,
  errorDiscountPercentMax: keys.coupons.errorDiscountPercentMax,
  errorBogoBuyQtyInvalid: keys.coupons.errorBogoBuyQtyInvalid,
  errorBogoGetQtyInvalid: keys.coupons.errorBogoGetQtyInvalid,
  errorBogoPercentInvalid: keys.coupons.errorBogoPercentInvalid,
  errorScopeRequired: keys.coupons.errorScopeRequired,
};

/** Convenience accessor for the discount-namespace promotion keys. */
export const discountPromotionKeys: PromotionFieldKeys = {
  kindLabel: keys.discounts.kindLabel,
  kindCartDiscount: keys.discounts.kindCartDiscount,
  kindProductDiscount: keys.discounts.kindProductDiscount,
  kindFreeDelivery: keys.discounts.kindFreeDelivery,
  kindBogo: keys.discounts.kindBogo,
  discountUnitLabel: keys.discounts.discountUnitLabel,
  discountUnitPercent: keys.discounts.discountUnitPercent,
  discountUnitFixed: keys.discounts.discountUnitFixed,
  discountValueLabel: keys.discounts.discountValueLabel,
  discountValuePercentPlaceholder:
    keys.discounts.discountValuePercentPlaceholder,
  discountValueFixedPlaceholder: keys.discounts.discountValueFixedPlaceholder,
  bogoBuyQtyLabel: keys.discounts.bogoBuyQtyLabel,
  bogoBuyQtyPlaceholder: keys.discounts.bogoBuyQtyPlaceholder,
  bogoGetQtyLabel: keys.discounts.bogoGetQtyLabel,
  bogoGetQtyPlaceholder: keys.discounts.bogoGetQtyPlaceholder,
  bogoGetPercentLabel: keys.discounts.bogoGetPercentLabel,
  bogoGetPercentHint: keys.discounts.bogoGetPercentHint,
  bogoGetPercentPlaceholder: keys.discounts.bogoGetPercentPlaceholder,
  scopeLabel: keys.discounts.scopeLabel,
  scopeAll: keys.discounts.scopeAll,
  scopeProducts: keys.discounts.scopeProducts,
  scopeCategories: keys.discounts.scopeCategories,
  scopeProductsLabel: keys.discounts.scopeProductsLabel,
  scopeCategoriesLabel: keys.discounts.scopeCategoriesLabel,
  scopePickerPlaceholder: keys.discounts.scopePickerPlaceholder,
  scopeSearchPlaceholder: keys.discounts.scopeSearchPlaceholder,
  scopeNoResults: keys.discounts.scopeNoResults,
  scopeProductsEmpty: keys.discounts.scopeProductsEmpty,
  scopeCategoriesEmpty: keys.discounts.scopeCategoriesEmpty,
  scopeLoading: keys.discounts.scopeLoading,
  errorDiscountValueRequired: keys.discounts.errorDiscountValueRequired,
  errorDiscountPercentMax: keys.discounts.errorDiscountPercentMax,
  errorBogoBuyQtyInvalid: keys.discounts.errorBogoBuyQtyInvalid,
  errorBogoGetQtyInvalid: keys.discounts.errorBogoGetQtyInvalid,
  errorBogoPercentInvalid: keys.discounts.errorBogoPercentInvalid,
  errorScopeRequired: keys.discounts.errorScopeRequired,
};
